/**
 * Saving ahead for the Speedy player (F-129; Tim, 2 Nov: "the first seconds of the next ~20").
 *
 * This part saves what is public and the same on every phone: the poster and the short preview
 * clip of the next 20 Speedies, through the shared prefetch loader, inside the Speedies' own 10 MB
 * (the lessons keep their 30 MB). Nothing is saved on Save-Data or a slower connection than 4G
 * (the loader's own rule).
 *
 * The opening seconds of the video itself are saved by the segment store (`segment-store.ts`),
 * started here for the same 20, one at a time, nearest first.
 */
import { useEffect } from 'react';
import type { SpeedyCard } from '@/api/speedies';
import { isConnectionAllowed, mediaPrefetchCoordinator, resolvePrefetchUrl, type PrefetchBudget } from '@/lib/media-prefetch';
import { saveOpenings } from './segment-store';

export const SPEEDY_AHEAD = 20;
/** Posters and clips; the segment store has the other 5 MB of the Speedies' 10 MB. */
export const SPEEDY_PREFETCH_BUDGET: PrefetchBudget = { key: 'speedy', limitBytes: 5 * 1024 * 1024 };
const PRIORITY = 4;
const OPENINGS_DELAY_MS = 1500;
const POSTER_BYTES = 60 * 1024;
/** A 240-wide Speedy clip (estimated: 480-wide course clips measured ≈ 500 KB; not yet measured at 240). */
const CLIP_BYTES = 200 * 1024;

/** The public files to save for the Speedies after `index`, nearest first. */
export function aheadUrls(items: SpeedyCard[], index: number, ahead = SPEEDY_AHEAD): { url: string; bytes: number }[] {
  const out: { url: string; bytes: number }[] = [];
  for (const c of items.slice(index + 1, index + 1 + ahead)) {
    // The small poster first: it's the one tiles show.
    const poster = resolvePrefetchUrl(c.small_poster_url ?? c.poster_url);
    if (poster) out.push({ url: poster, bytes: POSTER_BYTES });
    const clip = resolvePrefetchUrl(c.preview_clip_url);
    if (clip) out.push({ url: clip, bytes: CLIP_BYTES });
  }
  return out;
}

/** The Speedies after `index` whose opening can be saved (on Stream, not the member's own clip). */
export function aheadMediaIds(items: SpeedyCard[], index: number, ahead = SPEEDY_AHEAD): string[] {
  return items.slice(index + 1, index + 1 + ahead)
    .filter((c) => c.playable && !!c.media_id && !c.local_clip_url)
    .map((c) => c.media_id as string);
}

export function useSpeedyAheadPrefetch(items: SpeedyCard[], index: number): void {
  useEffect(() => {
    const ac = new AbortController();
    if (mediaPrefetchCoordinator) {
      for (const { url, bytes } of aheadUrls(items, index)) {
        mediaPrefetchCoordinator.enqueue(url, PRIORITY, bytes, ac.signal, SPEEDY_PREFETCH_BUDGET);
      }
    }
    // The openings start a moment later, so they don't compete with the Speedy now loading.
    const timer = setTimeout(() => {
      if (!isConnectionAllowed()) return;
      const ahead = aheadMediaIds(items, index);
      const current = items[index]?.media_id;
      void saveOpenings(ahead, ac.signal, new Set(current ? [current, ...ahead] : ahead));
    }, OPENINGS_DELAY_MS);
    return () => {
      clearTimeout(timer);
      ac.abort();
    };
  }, [items, index]);
}
